import type { AxiosInstance } from 'axios'
import { wrapApiCall } from '@/lib/axios'
import { deserialize } from 'ts-jackson'
import {
  AccessControlCardCredentialResponse,
  AccessControlDoor,
  ReplaceAccessControlCardCredentialPayload,
  ReplaceAccessControlCardCredentialResponse,
} from './model'
import { PageInfo, type PaginationOpt, type PaginationResponse } from '../model'

export const AccessControlService = (client: AxiosInstance) => {
  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

  const extractDoorItems = (payload: unknown): unknown[] => {
    if (Array.isArray(payload)) {
      return payload
    }
    if (typeof payload !== 'object' || payload === null) {
      return []
    }

    const directItems = (payload as { items?: unknown }).items
    if (Array.isArray(directItems)) {
      return directItems
    }

    const data = (payload as { data?: unknown }).data
    if (typeof data !== 'object' || data === null) {
      return []
    }

    const nestedItems = (data as { item?: unknown; items?: unknown }).item
    if (Array.isArray(nestedItems)) {
      return nestedItems
    }

    const altNestedItems = (data as { item?: unknown; items?: unknown }).items
    if (Array.isArray(altNestedItems)) {
      return altNestedItems
    }

    return []
  }

  return {
    async getDoors(opt: PaginationOpt): Promise<PaginationResponse<AccessControlDoor>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; items: Record<string, unknown>[] }>(
          '/access-control/doors',
          { params: opt }
        )
      )
      const items = Array.isArray(data?.items) ? data.items : []

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: items.map((item: Record<string, unknown>) => deserialize(item, AccessControlDoor)),
      }
    },
    async getDoorById(doorId: string): Promise<AccessControlDoor> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/access-control/doors/${doorId}`)
      )
      return deserialize(data, AccessControlDoor)
    },
    async getCardCredential(userId: string | number): Promise<AccessControlCardCredentialResponse> {
      const { data } = await wrapApiCall(() =>
        client.get<AccessControlCardCredentialResponse | null>(
          `/access-control/credentials/card/${encodeURIComponent(String(userId))}`
        )
      )
      return data ?? {}
    },
    async getUserDoors(userId: string | number): Promise<AccessControlDoor[]> {
      const { data } = await wrapApiCall(() =>
        client.get<unknown>(`/access-control/users/${encodeURIComponent(String(userId))}/doors`)
      )
      const items = extractDoorItems(data)
      return items.filter(isRecord).map((item) => deserialize(item, AccessControlDoor))
    },
    async replaceCardCredential(
      payload: ReplaceAccessControlCardCredentialPayload
    ): Promise<ReplaceAccessControlCardCredentialResponse> {
      const normalizedCardUid = payload.cardUid.trim()
      const { data } = await wrapApiCall(() =>
        client.post<ReplaceAccessControlCardCredentialResponse | null>(
          '/access-control/credentials/card/replace',
          {
            ...payload,
            cardUid: normalizedCardUid,
          }
        )
      )
      return data ?? {}
    },
  }
}
